import { Dispatch, SetStateAction, useCallback } from 'react'

import { isResponseError } from '@marketplace-web/core-api/api-client-util'
import { useSession } from '@marketplace-web/shared/session-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  getPhotos,
  createPhoto,
  transformMediaUploadPhotoDtos,
  transformMediaUploadPhotoDtosToOrientations,
  CreatePhotoResp,
} from '@marketplace-web/item-upload/media-upload-data'

import { createPhotoFormData } from '../utils'
import useMediaSelectContext from './useMediaSelectContext'

type Props = {
  photoEntryId: number | string | null
  tempUuid?: string
  type: string
  setUiState: Dispatch<SetStateAction<UiState>>
  photosCount: number
  onUploadFilesStart: () => void
  onUploadFilesEnd: (uiState: UiState, photosCount: number, userItemCount: number) => void
  onPhotoFetchError?: () => void
}

const useMediaSelectUtils = ({
  setUiState,
  tempUuid,
  photoEntryId,
  type,
  photosCount,
  onUploadFilesStart,
  onUploadFilesEnd,
  onPhotoFetchError,
}: Props) => {
  const { user } = useSession()
  const itemCount = user?.item_count || 0
  const { addPhotos } = useMediaSelectContext()

  const fetchPhotos = useCallback(async () => {
    if (!photoEntryId || !tempUuid) return

    setUiState(UiState.Pending)
    const response = await getPhotos({ type, photoEntryId, tempUuid })

    if (isResponseError(response)) {
      setUiState(UiState.Failure)

      return
    }

    setUiState(UiState.Success)
    const transformedPhotos = transformMediaUploadPhotoDtos(response.photos_dto)
    const transformedPhotoOrientations = transformMediaUploadPhotoDtosToOrientations(
      response.photos_dto,
    )
    addPhotos(transformedPhotos, transformedPhotoOrientations)
  }, [addPhotos, photoEntryId, setUiState, tempUuid, type])

  const uploadPhotoFiles = useCallback(
    async ({ files, uploadType }: { files: Array<File>; uploadType: string }) => {
      try {
        setUiState(UiState.Pending)
        onUploadFilesStart()

        const convertedPhotos = files.map(file => createPhotoFormData(file, uploadType, tempUuid))

        const responses = await Promise.all(convertedPhotos.map(createPhoto))

        const hasError = responses.some(response => isResponseError(response))

        if (hasError) {
          setUiState(UiState.Failure)
          onUploadFilesEnd(UiState.Failure, photosCount, itemCount)
          onPhotoFetchError?.()

          return
        }

        const transformedPhotos = transformMediaUploadPhotoDtos(responses as Array<CreatePhotoResp>)
        const transformedPhotoOrientations = transformMediaUploadPhotoDtosToOrientations(
          responses as Array<CreatePhotoResp>,
        )

        setUiState(UiState.Success)
        addPhotos(transformedPhotos, transformedPhotoOrientations)
        onUploadFilesEnd(UiState.Success, photosCount, itemCount)
      } catch (error) {
        setUiState(UiState.Failure)
        onUploadFilesEnd(UiState.Failure, photosCount, itemCount)
      }
    },
    [
      addPhotos,
      itemCount,
      photosCount,
      setUiState,
      tempUuid,
      onUploadFilesStart,
      onUploadFilesEnd,
      onPhotoFetchError,
    ],
  )

  return {
    fetchPhotos,
    uploadPhotoFiles,
  }
}

export default useMediaSelectUtils
